import { useCallback, useEffect, useRef, useState } from "react";
import { TrendUp, Pulse, ArrowsLeftRight, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";
import { Chapter, Reveal } from "./Reveal";

const POLL_MS = 60000;

function formatINR(value, { forceDecimals = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const hasFraction = Math.abs(value % 1) > 1e-9;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: forceDecimals || hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatIST(iso) {
  try {
    return (
      new Date(iso).toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      }) + " IST"
    );
  } catch {
    return null;
  }
}

export default function RateSection() {
  const [state, setState] = useState({ status: "loading", rate: null, updatedAt: null, stale: false });
  const [usdt, setUsdt] = useState("100");
  const [checking, setChecking] = useState(false);
  const lastGood = useRef(null);

  const fetchRate = useCallback((manual = false) => {
    if (manual) setChecking(true);
    fetch("/api/rate")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad status"))))
      .then((data) => {
        if (data?.available && typeof data.rate === "number") {
          const next = { status: "ready", rate: data.rate, updatedAt: data.updated_at, stale: Boolean(data.stale) };
          if (!data.stale) lastGood.current = next;
          setState(next);
        } else {
          setState(lastGood.current ? { ...lastGood.current, stale: true } : { status: "error", rate: null, updatedAt: null, stale: false });
        }
      })
      .catch(() => {
        setState(lastGood.current ? { ...lastGood.current, stale: true } : { status: "error", rate: null, updatedAt: null, stale: false });
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    fetchRate();
    const id = setInterval(() => fetchRate(), POLL_MS);
    return () => clearInterval(id);
  }, [fetchRate]);

  const receive = state.rate !== null && usdt !== "" && !Number.isNaN(Number(usdt))
    ? formatINR(Number(usdt) * state.rate)
    : null;

  return (
    <section id="rates" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="rate-section">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Chapter index="04" label="Rates" />
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl font-semibold tracking-tighter text-paper sm:text-5xl" data-testid="rate-heading">
              Current market rates,{" "}
              <span className="text-brand-400">always in the open.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-mist sm:text-lg">
              Indicative range <strong className="text-paper">₹110–₹115 / USDT</strong>. The number
              on the card is fetched live from the same source that powers the Rupora app.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-8 space-y-3.5">
              {["Live rate synced with the app", "No hidden conversion steps", "Final rate confirmed before processing"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm font-medium text-paper/75">
                  <span className="h-px w-6 bg-brand-500" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="relative" data-testid="rate-card">
              <div className="absolute -inset-8 rounded-[3rem] bg-brand-500/15 blur-3xl" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[2rem] border border-line bg-pine p-8 sm:p-10">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-mist">
                    <Pulse size={15} weight="duotone" className="text-brand-400" /> Current Market Rate
                  </span>
                  {state.status === "ready" && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-500/15 px-3 py-1 text-[11px] font-semibold text-brand-300" data-testid="rate-live-badge">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-400" /> {state.stale ? "Last known" : "Live"}
                    </span>
                  )}
                </div>

                <div className="mt-9">
                  {state.status === "loading" && (
                    <div data-testid="rate-loading">
                      <div className="shimmer h-16 w-56 rounded-xl" />
                      <p className="mt-3 text-sm text-mist">Loading current rate...</p>
                    </div>
                  )}

                  {state.status === "error" && (
                    <div data-testid="rate-error">
                      <p className="inline-flex items-center gap-2.5 font-display text-2xl font-semibold text-brand-300">
                        <WarningCircle size={26} weight="duotone" /> Rate temporarily unavailable
                      </p>
                      <p className="mt-3 text-sm text-mist">Please try again in a moment.</p>
                    </div>
                  )}

                  {state.status === "ready" && (
                    <div className="flex items-end gap-4">
                      <div>
                        <p className="font-display text-6xl font-semibold tracking-tight text-brand-300 sm:text-7xl" data-testid="rate-value">
                          {formatINR(state.rate, { forceDecimals: true })}
                        </p>
                        <p className="mt-2.5 font-mono text-sm text-mist" data-testid="rate-pair">
                          1 USDT = {formatINR(state.rate, { forceDecimals: true })}
                        </p>
                      </div>
                      <TrendUp size={30} weight="duotone" className="mb-3 text-brand-400" />
                    </div>
                  )}

                  <p className="mt-4 text-xs leading-relaxed text-mist/70">
                    Indicative rate only. Final rate depends on market conditions, volume, fund
                    type and transaction verification.
                  </p>
                </div>

                <div className="mt-7 rounded-2xl bg-night/70 p-5" data-testid="rate-calculator">
                  <div className="flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-mist">
                    <ArrowsLeftRight size={14} weight="duotone" className="text-brand-400" /> Quick estimate
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="usdt-amount" className="text-xs text-mist">USDT amount</label>
                      <input
                        id="usdt-amount"
                        data-testid="rate-calculator-input"
                        type="number"
                        min="0"
                        value={usdt}
                        onChange={(e) => setUsdt(e.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-line bg-pine px-4 py-3 font-mono text-lg text-paper outline-none transition-colors focus:border-brand-500"
                        placeholder="100"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-mist">You receive</span>
                      <p className="mt-1.5 rounded-xl border border-brand-500/25 bg-brand-500/10 px-4 py-3 font-mono text-lg font-semibold text-brand-300" data-testid="rate-calculator-result">
                        {state.status === "ready" && receive ? receive : "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-mist/70" data-testid="rate-updated-at">
                    {state.status === "ready" && state.updatedAt
                      ? `${state.stale ? "Last updated" : "Updated at"} ${formatIST(state.updatedAt)}`
                      : "Synced with the live Rupora rate"}
                  </p>
                  <button
                    onClick={() => fetchRate(true)}
                    disabled={checking}
                    data-testid="check-rate-button"
                    className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:brightness-110 disabled:opacity-70"
                  >
                    <ArrowClockwise size={16} weight="bold" className={checking ? "animate-spin" : ""} />
                    {checking ? "Checking..." : "Check Current Rate"}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
